/**
 * leadLanding — friendly display labels for lead.landing_source.
 *
 * Backend canonicalizes incoming values via normalize_landing_source(), but
 * legacy / external integrations may have written other values (manual,
 * meta_ads, etc.). We keep the canonicalization in the backend but resolve
 * any historical value to a friendly Spanish/English label here.
 *
 * No data migration — frontend display only. Unknown / missing values render
 * as "Origen no registrado" (instead of the previous "Landing desconocida").
 */

// Canonical → Spanish/English label.
export const LANDING_LABEL = {
  landing_main:       "Landing principal",
  landing_formulario: "Landing formulario",
  // New canonical values — one per public landing URL.
  landing_1_lp:       "Landing 1 — /lp",
  landing_2_lpage:    "Landing 2 — /lpage",
  investor_referral:  "Referido de inversor",
  meta_ads:           "Meta Ads",
  website:            "Web orgánica",
  manual:             "Manual",
  unknown:            "Origen no registrado",
  other:              "Otro origen",
};

// Aliases — values that may exist in legacy data but should display as a
// canonical label.
const LANDING_ALIASES = {
  // Main / landing
  main_landing:    "landing_main",
  website_landing: "landing_main",
  landing:         "landing_main",
  home:            "landing_main",
  principal:       "landing_main",
  // Formulario
  form:        "landing_formulario",
  formulario:  "landing_formulario",
  // Landing 1 — /lp
  lp:          "landing_1_lp",
  "1":         "landing_1_lp",
  landing1:    "landing_1_lp",
  landing_1:   "landing_1_lp",
  // Landing 2 — /lpage
  lpage:       "landing_2_lpage",
  "2":         "landing_2_lpage",
  landing2:    "landing_2_lpage",
  landing_2:   "landing_2_lpage",
  // Referral
  referral:    "investor_referral",
  referido:    "investor_referral",
  // Ads
  facebook_ads:  "meta_ads",
  instagram_ads: "meta_ads",
  fb_ads:        "meta_ads",
  ig_ads:        "meta_ads",
  // Organic
  organic_web: "website",
  web:         "website",
  organic:     "website",
};

/**
 * Resolve an arbitrary landing_source value to a friendly label.
 * Empty / null / undefined → "Origen no registrado".
 * Unknown string → "Otro origen".
 */
export function landingLabel(value) {
  if (value == null || value === "") return LANDING_LABEL.unknown;
  const v = String(value).toLowerCase().trim().replace(/-/g, "_").replace(/\s+/g, "_");
  if (LANDING_LABEL[v]) return LANDING_LABEL[v];
  const canonical = LANDING_ALIASES[v];
  if (canonical && LANDING_LABEL[canonical]) return LANDING_LABEL[canonical];
  return LANDING_LABEL.other;
}

/**
 * Returns the canonical key (post-alias resolution) for a given raw value.
 * Useful for tone selection or test-id namespacing.
 */
export function landingKey(value) {
  if (value == null || value === "") return "unknown";
  const v = String(value).toLowerCase().trim().replace(/-/g, "_").replace(/\s+/g, "_");
  if (LANDING_LABEL[v]) return v;
  return LANDING_ALIASES[v] || "other";
}

/** True for investor referrals — used to give the chip a distinct tone. */
export function isInvestorReferral(value) {
  return landingKey(value) === "investor_referral";
}
